import React, { useState, useEffect } from 'react';
import { Mail, Eye, Trash2, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminContactSubmissions = ({ token }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      // Get all contact submissions
      const response = await axios.get(`${API_URL}/contact/submissions`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(
        `${API_URL}/contact/submissions/${id}/status`,
        { status: 'read' },
        { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` } }
      );
      fetchSubmissions();
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await axios.delete(`${API_URL}/contact/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      fetchSubmissions();
      setSelectedSubmission(null);
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Contact Form Submissions</h2>

      {submissions.length > 0 ? (
        <div className="grid gap-4">
          {submissions.map((submission) => (
            <Card key={submission.id} className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{submission.name}</h3>
                        <p className="text-sm text-muted-foreground">{submission.email}</p>
                      </div>
                      <Badge variant={submission.status === 'new' ? 'default' : 'secondary'}>
                        {submission.status === 'new' ? 'New' : 'Read'}
                      </Badge>
                    </div>
                    
                    <div className="mb-3">
                      <p className="font-medium text-sm text-muted-foreground">Subject:</p>
                      <p className="font-semibold">{submission.subject}</p>
                    </div>

                    <div className="mb-3">
                      <p className="font-medium text-sm text-muted-foreground">Message:</p>
                      <p className="text-muted-foreground leading-relaxed">
                        {submission.message}
                      </p>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Received: {new Date(submission.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {submission.status === 'new' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(submission.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteSubmission(submission.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No contact submissions yet</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};